import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useFetch from "hooks/useFetch";
import useLeaveGuard from "hooks/useLeaveGuard";
import {
  DISCARD_CHANGES_CONFIRM,
  useBeforeUnload,
} from "pages/CampaignPage/unsavedChanges";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Select,
  Skeleton,
  TextField,
  Textarea,
  Toggle,
  useConfirm,
  useToast,
} from "stories/Admin";
import AdhanPhrasesEditor from "./AdhanPhrasesEditor";
import DuaPicker from "./DuaPicker";
import KidsAudioField from "./KidsAudioField";
import KidsPreview from "./KidsPreview";
import {
  AdminCurriculum,
  AdminItem,
  AdminLevel,
  COLOR_LABELS,
  CopySummary,
  dropItem,
  errorText,
  formToRequest,
  ICON_EMOJI,
  ICON_LABELS,
  isDirty,
  ITEM_TYPES,
  itemOrder,
  itemToForm,
  ItemForm,
  KIDS_API,
  KidsDua,
  LEARN_KEYS,
  LEARN_LABELS,
  LEVEL_COLORS,
  LEVEL_ICONS,
  levelFormToRequest,
  LevelForm,
  levelToForm,
  move,
  newItemForm,
  newLevelForm,
  TYPE_LABELS,
  validateItemForm,
} from "./kidsTypes";
import styles from "./KidsPage.module.scss";

type Editing =
  | {
      kind: "item";
      isNew: boolean;
      initial: ItemForm;
      draft: ItemForm;
      saved?: AdminItem;
    }
  | { kind: "level"; isNew: boolean; initial: LevelForm; draft: LevelForm }
  | null;

type Drag = { kind: "item" | "level"; id: string } | null;

/** Superadmin «Barn»: læringsstien for barn i appene (nivåer, stasjoner, barnelyd og adhan). */
const KidsPage: React.FC = () => {
  const list = useFetch<AdminCurriculum>();
  const save = useFetch<any>();
  const listRef = useRef(list.execute);
  listRef.current = list.execute;
  const toast = useToast();
  const confirm = useConfirm();

  const [data, setData] = useState<AdminCurriculum | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [selectedDua, setSelectedDua] = useState<KidsDua | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [drag, setDrag] = useState<Drag>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [summary, setSummary] = useState<CopySummary | null>(null);
  const [copyingAll, setCopyingAll] = useState(false);

  const dirty = !!editing && isDirty<unknown>(editing.initial, editing.draft);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useLeaveGuard(() => dirtyRef.current, DISCARD_CHANGES_CONFIRM);
  useBeforeUnload(dirty);

  const load = useCallback(async () => {
    const result = await listRef.current(`${KIDS_API}/curriculum`, "GET");
    if (result) {
      setData(result);
      setLoadError(null);
    } else {
      setLoadError("Kunne ikke hente læringsstien.");
    }
    return result;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const levels = useMemo(
    () => [...(data?.levels ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [data],
  );
  const order = useMemo(
    () => itemOrder(levels, data?.items ?? []),
    [levels, data],
  );
  const itemsById = useMemo(
    () => Object.fromEntries((data?.items ?? []).map((i) => [i.id, i])),
    [data],
  );

  /** Spør før ulagrede endringer forkastes. */
  const canLeaveEditor = async () =>
    !dirtyRef.current || confirm(DISCARD_CHANGES_CONFIRM);

  const openItem = async (item: AdminItem) => {
    if (
      editing?.kind === "item" &&
      editing.draft.id === item.id &&
      !editing.isNew
    )
      return;
    if (!(await canLeaveEditor())) return;
    const form = itemToForm(item);
    setEditing({
      kind: "item",
      isNew: false,
      initial: form,
      draft: form,
      saved: item,
    });
    setSelectedDua(item.dua ?? null);
    setErrors([]);
  };

  const openNewItem = async (levelId?: string) => {
    if (!(await canLeaveEditor())) return;
    const form = newItemForm(levelId ?? levels[0]?.id ?? "");
    setEditing({ kind: "item", isNew: true, initial: form, draft: form });
    setSelectedDua(null);
    setErrors([]);
  };

  const openLevel = async (level?: AdminLevel) => {
    if (!(await canLeaveEditor())) return;
    const form = level ? levelToForm(level) : newLevelForm();
    setEditing({ kind: "level", isNew: !level, initial: form, draft: form });
    setErrors([]);
  };

  const closeEditor = async () => {
    if (!(await canLeaveEditor())) return;
    setEditing(null);
  };

  const setItemDraft = (patch: Partial<ItemForm>) =>
    setEditing((e) =>
      e?.kind === "item" ? { ...e, draft: { ...e.draft, ...patch } } : e,
    );
  const setLevelDraft = (patch: Partial<LevelForm>) =>
    setEditing((e) =>
      e?.kind === "level" ? { ...e, draft: { ...e.draft, ...patch } } : e,
    );

  const saveItem = async () => {
    if (editing?.kind !== "item") return;
    const problems = validateItemForm(editing.draft);
    setErrors(problems);
    if (problems.length) return;
    setSaving(true);
    try {
      const body = formToRequest(editing.draft, editing.isNew);
      const saved: AdminItem | null = editing.isNew
        ? await save.execute(`${KIDS_API}/items`, "POST", body)
        : await save.execute(
            `${KIDS_API}/items/${encodeURIComponent(editing.draft.id)}`,
            "PUT",
            body,
          );
      if (!saved) {
        setErrors([errorText(save.lastError(), "Kunne ikke lagre stasjonen.")]);
        return;
      }
      const form = itemToForm(saved);
      setEditing({
        kind: "item",
        isNew: false,
        initial: form,
        draft: form,
        saved,
      });
      setSelectedDua(saved.dua ?? null);
      toast.success(
        saved.hiddenReason
          ? `Lagret, men vises ikke i appen: ${saved.hiddenReason}`
          : "Stasjonen er lagret.",
      );
      await load();
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async () => {
    if (editing?.kind !== "item" || editing.isNew) return;
    const ok = await confirm({
      title: "Slette stasjonen?",
      message:
        "Stasjonen fjernes fra læringsstien i appene. Barn som har lært den, beholder stjernene. Vil du heller skjule den, slå av «Aktiv».",
      confirmLabel: "Slett",
      tone: "danger",
    });
    if (!ok) return;
    const result = await save.execute(
      `${KIDS_API}/items/${encodeURIComponent(editing.draft.id)}`,
      "DELETE",
    );
    if (result === null && save.lastError()) {
      toast.error(errorText(save.lastError(), "Kunne ikke slette stasjonen."));
      return;
    }
    setEditing(null);
    toast.success("Stasjonen er slettet.");
    load();
  };

  const saveLevel = async () => {
    if (editing?.kind !== "level") return;
    if (!editing.draft.title.trim()) {
      setErrors(["Tittel mangler."]);
      return;
    }
    setSaving(true);
    try {
      const body = levelFormToRequest(editing.draft, editing.isNew);
      const saved: AdminLevel | null = editing.isNew
        ? await save.execute(`${KIDS_API}/levels`, "POST", body)
        : await save.execute(
            `${KIDS_API}/levels/${encodeURIComponent(editing.draft.id)}`,
            "PUT",
            body,
          );
      if (!saved) {
        setErrors([errorText(save.lastError(), "Kunne ikke lagre nivået.")]);
        return;
      }
      const form = levelToForm(saved);
      setEditing({ kind: "level", isNew: false, initial: form, draft: form });
      setErrors([]);
      toast.success("Nivået er lagret.");
      await load();
    } finally {
      setSaving(false);
    }
  };

  const deleteLevel = async () => {
    if (editing?.kind !== "level" || editing.isNew) return;
    const ok = await confirm({
      title: "Slette nivået?",
      message:
        "Bare tomme nivåer kan slettes. Flytt eller slett stasjonene først, eller skjul nivået.",
      confirmLabel: "Slett",
      tone: "danger",
    });
    if (!ok) return;
    const result = await save.execute(
      `${KIDS_API}/levels/${encodeURIComponent(editing.draft.id)}`,
      "DELETE",
    );
    if (result === null && save.lastError()) {
      toast.error(errorText(save.lastError(), "Kunne ikke slette nivået."));
      return;
    }
    setEditing(null);
    toast.success("Nivået er slettet.");
    load();
  };

  /** Lagrer ny rekkefølge og holder skjemaet som er åpent i takt (nivå kan endres ved flytting). */
  const saveOrder = async (body: {
    levels?: string[];
    items?: Record<string, string[]>;
  }) => {
    const result: AdminCurriculum | null = await save.execute(
      `${KIDS_API}/order`,
      "PUT",
      body,
    );
    if (!result) {
      toast.error(errorText(save.lastError(), "Kunne ikke lagre rekkefølgen."));
      return;
    }
    setData(result);
    setEditing((e) => {
      if (e?.kind !== "item" || e.isNew) return e;
      const moved = result.items.find((i) => i.id === e.draft.id);
      if (!moved || moved.levelId === e.initial.levelId) return e;
      return {
        ...e,
        initial: { ...e.initial, levelId: moved.levelId },
        draft: { ...e.draft, levelId: moved.levelId },
      };
    });
  };

  const onDropItem = (levelId: string, beforeId: string | null) => {
    if (drag?.kind !== "item" || drag.id === beforeId) return;
    saveOrder({ items: dropItem(order, drag.id, levelId, beforeId) });
  };

  const onDropLevel = (beforeId: string) => {
    if (drag?.kind !== "level" || drag.id === beforeId) return;
    const ids = levels.map((l) => l.id);
    saveOrder({
      levels: move(ids, ids.indexOf(drag.id), ids.indexOf(beforeId)),
    });
  };

  const moveItem = (item: AdminItem, delta: number) => {
    const ids = order[item.levelId] ?? [];
    const at = ids.indexOf(item.id);
    if (at + delta < 0 || at + delta >= ids.length) return;
    saveOrder({ items: { [item.levelId]: move(ids, at, at + delta) } });
  };

  const toggleItemActive = async (item: AdminItem) => {
    const body = {
      ...formToRequest(itemToForm(item), false),
      active: !item.active,
    };
    const saved: AdminItem | null = await save.execute(
      `${KIDS_API}/items/${encodeURIComponent(item.id)}`,
      "PUT",
      body,
    );
    if (!saved) {
      toast.error(errorText(save.lastError(), "Kunne ikke endre stasjonen."));
      return;
    }
    setEditing((e) =>
      e?.kind === "item" && !e.isNew && e.draft.id === item.id
        ? {
            ...e,
            initial: { ...e.initial, active: saved.active },
            draft: { ...e.draft, active: saved.active },
          }
        : e,
    );
    load();
  };

  const toggleLevelActive = async (level: AdminLevel) => {
    // Hele nivået sendes (PUT erstatter alle feltene).
    const saved = await save.execute(
      `${KIDS_API}/levels/${encodeURIComponent(level.id)}`,
      "PUT",
      {
        ...levelFormToRequest(levelToForm(level), false),
        active: !level.active,
      },
    );
    if (!saved)
      toast.error(errorText(save.lastError(), "Kunne ikke endre nivået."));
    load();
  };

  /** «Lag egen barnekopi av lyden» for stasjonen som er åpen. Andre ulagrede endringer beholdes. */
  const copyDuaAudio = async () => {
    if (editing?.kind !== "item" || editing.isNew) return;
    const saved: AdminItem | null = await save.execute(
      `${KIDS_API}/items/${encodeURIComponent(editing.draft.id)}/audio/copy-from-dua`,
      "POST",
    );
    if (!saved) {
      toast.error(errorText(save.lastError(), "Kunne ikke kopiere lyden."));
      return;
    }
    const url = saved.audioUrlOverride ?? "";
    setEditing((e) =>
      e?.kind === "item"
        ? {
            ...e,
            saved,
            initial: { ...e.initial, audioUrlOverride: url },
            draft: { ...e.draft, audioUrlOverride: url },
          }
        : e,
    );
    toast.success(
      "Barnet har nå sin egen kopi av lyden. Den vanlige duaen er uendret.",
    );
    load();
  };

  const copyAllDuaAudio = async () => {
    const ok = await confirm({
      title: "Lage barnekopier av all dua-lyd?",
      message:
        "Lyden til hver dua i barnestien som ikke har egen barnelyd, kopieres til en egen fil for barn. Etterpå kan du bytte barnelyden uten at lyden under «Duaer» endres. Kan kjøres flere ganger.",
      confirmLabel: "Lag kopier",
    });
    if (!ok) return;
    setCopyingAll(true);
    try {
      const result: CopySummary | null = await save.execute(
        `${KIDS_API}/audio/copy-from-duas`,
        "POST",
      );
      if (!result) {
        toast.error(errorText(save.lastError(), "Kunne ikke lage kopiene."));
        return;
      }
      setSummary(result);
      const fresh = await load();
      // Skjemaet som er åpent, får den nye lyden uten å miste andre endringer.
      const updated = fresh?.items.find(
        (i) => editing?.kind === "item" && i.id === editing.draft.id,
      );
      if (updated) {
        const url = updated.audioUrlOverride ?? "";
        setEditing((e) =>
          e?.kind === "item" && !e.isNew
            ? {
                ...e,
                initial: { ...e.initial, audioUrlOverride: url },
                draft: { ...e.draft, audioUrlOverride: url },
              }
            : e,
        );
      }
    } finally {
      setCopyingAll(false);
    }
  };

  const editingItemId =
    editing?.kind === "item" && !editing.isNew ? editing.draft.id : null;
  const editingLevelId =
    editing?.kind === "level" && !editing.isNew ? editing.draft.id : null;
  const levelOf = (id: string) => levels.find((l) => l.id === id);

  return (
    <>
      <PageHeader
        title="iBønn Kids"
        subtitle="Læringsstien for barn i appene: duaer, suraer, Lær å be og adhan. Barnas tekster og lyd endrer aldri innholdet under «Duaer»."
        actions={
          <>
            <Button
              variant="secondary"
              loading={copyingAll}
              onClick={copyAllDuaAudio}
            >
              Lag barnekopier av all dua-lyd
            </Button>
            <Button
              variant="secondary"
              icon={<Icon name="plus" size={16} />}
              onClick={() => openLevel()}
            >
              Nytt nivå
            </Button>
            <Button
              icon={<Icon name="plus" size={16} />}
              onClick={() => openNewItem()}
              disabled={!levels.length}
            >
              Ny stasjon
            </Button>
          </>
        }
      />

      {summary && (
        <Alert
          tone={summary.failed.length ? "warn" : "ok"}
          className={styles.summary}
        >
          <b>Barnekopier av dua-lyd:</b> {summary.copied.length} kopiert,{" "}
          {summary.skipped.length} hoppet over, {summary.failed.length} feilet.
          {summary.failed.length > 0 && (
            <ul>
              {summary.failed.map((f) => (
                <li key={f.id}>
                  {f.id}: {f.reason}
                </li>
              ))}
            </ul>
          )}
          {summary.skipped.some(
            (s) => s.reason !== "Har allerede egen barnelyd",
          ) && (
            <ul>
              {summary.skipped
                .filter((s) => s.reason !== "Har allerede egen barnelyd")
                .map((s) => (
                  <li key={s.id}>
                    {s.id}: {s.reason}
                  </li>
                ))}
            </ul>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSummary(null)}>
            Lukk
          </Button>
        </Alert>
      )}

      {loadError && <Alert tone="error">{loadError}</Alert>}

      <div className={styles.layout}>
        <div className={styles.listCol}>
          {!data && list.isLoading && (
            <Card>
              <Skeleton height={20} />
              <Skeleton height={20} />
              <Skeleton height={20} />
            </Card>
          )}
          {data && levels.length === 0 && (
            <EmptyState
              title="Ingen nivåer ennå"
              action={<Button onClick={() => openLevel()}>Nytt nivå</Button>}
            />
          )}
          {levels.map((level) => (
            <div
              key={level.id}
              className={`${styles.level} ${dropTarget === `level:${level.id}` ? styles.dropOn : ""}`}
              onDragOver={(e) => {
                if (!drag) return;
                e.preventDefault();
                setDropTarget(`level:${level.id}`);
              }}
              onDragLeave={() => setDropTarget(null)}
              onDrop={(e) => {
                e.preventDefault();
                setDropTarget(null);
                if (drag?.kind === "level") onDropLevel(level.id);
                else onDropItem(level.id, null);
                setDrag(null);
              }}
            >
              <div
                className={`${styles.levelHead} ${styles[`tone_${level.color || "mint"}`] ?? ""} ${
                  editingLevelId === level.id ? styles.selected : ""
                }`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "move";
                  setDrag({ kind: "level", id: level.id });
                }}
                onDragEnd={() => setDrag(null)}
              >
                <span
                  className={styles.handle}
                  aria-hidden="true"
                  title="Dra for å flytte"
                >
                  ⋮⋮
                </span>
                <span className={styles.levelIcon} aria-hidden="true">
                  {ICON_EMOJI[level.icon ?? ""] ?? "⭐"}
                </span>
                <button
                  type="button"
                  className={styles.levelTitle}
                  onClick={() => openLevel(level)}
                >
                  {level.title}
                </button>
                {!level.active && <Badge tone="grey">Skjult</Badge>}
                <Toggle
                  aria-label={`${level.title} aktiv`}
                  checked={level.active}
                  onChange={() => toggleLevelActive(level)}
                />
                <IconButton
                  size="sm"
                  label={`Ny stasjon i ${level.title}`}
                  icon={<Icon name="plus" size={14} />}
                  onClick={() => openNewItem(level.id)}
                />
              </div>
              <ul className={styles.items}>
                {(order[level.id] ?? []).map((id, index, ids) => {
                  const item = itemsById[id];
                  if (!item) return null;
                  return (
                    <li
                      key={id}
                      className={`${styles.item} ${editingItemId === id ? styles.selected : ""} ${
                        !item.active || item.hiddenReason ? styles.dim : ""
                      } ${dropTarget === `item:${id}` ? styles.dropBefore : ""}`}
                      draggable
                      onDragStart={(e) => {
                        e.stopPropagation();
                        e.dataTransfer.effectAllowed = "move";
                        setDrag({ kind: "item", id });
                      }}
                      onDragEnd={() => setDrag(null)}
                      onDragOver={(e) => {
                        if (drag?.kind !== "item") return;
                        e.preventDefault();
                        e.stopPropagation();
                        setDropTarget(`item:${id}`);
                      }}
                      onDrop={(e) => {
                        if (drag?.kind !== "item") return;
                        e.preventDefault();
                        e.stopPropagation();
                        setDropTarget(null);
                        onDropItem(level.id, id);
                        setDrag(null);
                      }}
                    >
                      <span
                        className={styles.handle}
                        aria-hidden="true"
                        title="Dra for å flytte"
                      >
                        ⋮⋮
                      </span>
                      <button
                        type="button"
                        className={styles.itemMain}
                        onClick={() => openItem(item)}
                      >
                        <span className={styles.itemEmoji} aria-hidden="true">
                          {item.emoji || "⭐"}
                        </span>
                        <span className={styles.itemText}>
                          <span className={styles.itemTitle}>
                            {item.kidTitle}
                          </span>
                          <span className={styles.itemMeta}>
                            {TYPE_LABELS[item.type] ?? item.type} · {item.id} ·{" "}
                            {"★".repeat(item.stars)}
                          </span>
                        </span>
                      </button>
                      {item.audioUrlOverride && (
                        <Badge tone="mint" className={styles.itemBadge}>
                          Egen lyd
                        </Badge>
                      )}
                      {item.active && item.hiddenReason && (
                        <Badge tone="gold" className={styles.itemBadge}>
                          Vises ikke
                        </Badge>
                      )}
                      <span className={styles.itemMove}>
                        <IconButton
                          size="sm"
                          label="Flytt opp"
                          icon={<span aria-hidden="true">↑</span>}
                          disabled={index === 0}
                          onClick={() => moveItem(item, -1)}
                        />
                        <IconButton
                          size="sm"
                          label="Flytt ned"
                          icon={<span aria-hidden="true">↓</span>}
                          disabled={index === ids.length - 1}
                          onClick={() => moveItem(item, 1)}
                        />
                      </span>
                      <Toggle
                        aria-label={`${item.kidTitle} aktiv`}
                        checked={item.active}
                        onChange={() => toggleItemActive(item)}
                      />
                    </li>
                  );
                })}
                {(order[level.id] ?? []).length === 0 && (
                  <li className={styles.muted}>Ingen stasjoner. Dra en hit.</li>
                )}
              </ul>
            </div>
          ))}
        </div>

        <div className={styles.editCol}>
          {!editing && (
            <Card>
              <EmptyState title="Velg en stasjon eller et nivå">
                Klikk på en stasjon for å endre tekster, dua, overstyringer og
                lyd. Dra i ⋮⋮ for å endre rekkefølgen.
              </EmptyState>
            </Card>
          )}

          {editing?.kind === "level" && (
            <Card
              title={
                editing.isNew ? "Nytt nivå" : `Nivå: ${editing.initial.title}`
              }
              actions={
                <IconButton
                  label="Lukk"
                  icon={<Icon name="x" size={16} />}
                  onClick={closeEditor}
                />
              }
            >
              {errors.length > 0 && (
                <Alert tone="error">{errors.join(" ")}</Alert>
              )}
              {editing.isNew && (
                <TextField
                  label="Id (valgfri)"
                  help="a-z, 0-9 og -. Lages fra tittelen når den er tom. Kan ikke endres senere."
                  value={editing.draft.id}
                  onChange={(e) => setLevelDraft({ id: e.target.value })}
                />
              )}
              <div className={styles.row2}>
                <TextField
                  label="Tittel (norsk)"
                  required
                  value={editing.draft.title}
                  onChange={(e) => setLevelDraft({ title: e.target.value })}
                />
                <TextField
                  label="Tittel (engelsk)"
                  value={editing.draft.titleEn}
                  onChange={(e) => setLevelDraft({ titleEn: e.target.value })}
                />
                <TextField
                  label="Tittel (urdu)"
                  lang="ur"
                  dir="rtl"
                  value={editing.draft.titleUr}
                  onChange={(e) => setLevelDraft({ titleUr: e.target.value })}
                />
                <TextField
                  label="Tittel (arabisk)"
                  lang="ar"
                  dir="rtl"
                  value={editing.draft.titleAr}
                  onChange={(e) => setLevelDraft({ titleAr: e.target.value })}
                />
                <Select
                  label="Ikon"
                  value={editing.draft.icon}
                  onChange={(e) => setLevelDraft({ icon: e.target.value })}
                  options={LEVEL_ICONS.map((i) => ({
                    value: i,
                    label: `${ICON_EMOJI[i]} ${ICON_LABELS[i]}`,
                  }))}
                />
                <Select
                  label="Farge"
                  value={editing.draft.color}
                  onChange={(e) => setLevelDraft({ color: e.target.value })}
                  options={LEVEL_COLORS.map((c) => ({
                    value: c,
                    label: COLOR_LABELS[c],
                  }))}
                />
              </div>
              <Toggle
                label="Aktiv (vises i appene)"
                checked={editing.draft.active}
                onChange={(e) => setLevelDraft({ active: e.target.checked })}
              />
              <div className={styles.editorActions}>
                <Button
                  onClick={saveLevel}
                  loading={saving}
                  disabled={!dirty && !editing.isNew}
                >
                  Lagre
                </Button>
                <Button variant="ghost" onClick={closeEditor}>
                  Avbryt
                </Button>
                <span className={styles.spacer} />
                {!editing.isNew && (
                  <Button
                    variant="danger"
                    icon={<Icon name="trash" size={14} />}
                    onClick={deleteLevel}
                  >
                    Slett nivået
                  </Button>
                )}
              </div>
            </Card>
          )}

          {editing?.kind === "item" && (
            <ItemEditor
              editing={editing}
              levels={levels}
              dirty={dirty}
              saving={saving}
              errors={errors}
              selectedDua={selectedDua}
              defaultAdhanAudioUrl={data?.defaultAdhanAudioUrl}
              levelColor={levelOf(editing.draft.levelId)?.color}
              onChange={setItemDraft}
              onSelectDua={(d) => {
                setSelectedDua(d);
                setItemDraft({ duaId: d.id });
              }}
              onSave={saveItem}
              onClose={closeEditor}
              onDelete={deleteItem}
              onCopyDuaAudio={copyDuaAudio}
            />
          )}
        </div>
      </div>
    </>
  );
};

interface ItemEditorProps {
  editing: Extract<Editing, { kind: "item" }>;
  levels: AdminLevel[];
  dirty: boolean;
  saving: boolean;
  errors: string[];
  selectedDua: KidsDua | null;
  defaultAdhanAudioUrl?: string | null;
  levelColor?: string | null;
  onChange: (patch: Partial<ItemForm>) => void;
  onSelectDua: (dua: KidsDua) => void;
  onSave: () => void;
  onClose: () => void;
  onDelete: () => void;
  onCopyDuaAudio: () => void;
}

const ItemEditor: React.FC<ItemEditorProps> = ({
  editing,
  levels,
  dirty,
  saving,
  errors,
  selectedDua,
  defaultAdhanAudioUrl,
  levelColor,
  onChange,
  onSelectDua,
  onSave,
  onClose,
  onDelete,
  onCopyDuaAudio,
}) => {
  const f = editing.draft;
  const saved = editing.saved;
  const placeholder = (v?: string | null) =>
    v
      ? `Tom = duaens tekst: ${v.slice(0, 60)}${v.length > 60 ? " …" : ""}`
      : "Tom = duaens tekst";

  return (
    <div className={styles.editorGrid}>
      <Card
        title={
          editing.isNew
            ? "Ny stasjon"
            : `${f.emoji || "⭐"} ${editing.initial.kidTitle}`
        }
        subtitle={editing.isNew ? undefined : f.id}
        actions={
          <IconButton
            label="Lukk"
            icon={<Icon name="x" size={16} />}
            onClick={onClose}
          />
        }
      >
        {errors.length > 0 && <Alert tone="error">{errors.join(" ")}</Alert>}
        {!editing.isNew && saved?.hiddenReason && (
          <Alert tone="warn">
            Vises ikke i appene nå: {saved.hiddenReason}
          </Alert>
        )}

        <div className={styles.row3}>
          <Select
            label="Type"
            value={f.type}
            onChange={(e) =>
              onChange({ type: e.target.value as ItemForm["type"] })
            }
            options={ITEM_TYPES.map((t) => ({
              value: t,
              label: TYPE_LABELS[t],
            }))}
          />
          <Select
            label="Nivå"
            value={f.levelId}
            onChange={(e) => onChange({ levelId: e.target.value })}
            options={levels.map((l) => ({ value: l.id, label: l.title }))}
          />
          {editing.isNew ? (
            <TextField
              label="Id (valgfri)"
              help="Lages automatisk. Kan ikke endres senere (appene lagrer den i barnets fremgang)."
              value={f.id}
              onChange={(e) => onChange({ id: e.target.value })}
            />
          ) : (
            <TextField
              label="Id"
              value={f.id}
              readOnly
              help="Kan ikke endres (barnas fremgang bruker den)."
            />
          )}
        </div>

        <h4 className={styles.section}>Tekster for barn</h4>
        <div className={styles.row2}>
          <TextField
            label="Tittel (norsk)"
            required
            maxLength={80}
            value={f.kidTitle}
            onChange={(e) => onChange({ kidTitle: e.target.value })}
          />
          <TextField
            label="Tittel (engelsk)"
            maxLength={80}
            value={f.kidTitleEn}
            onChange={(e) => onChange({ kidTitleEn: e.target.value })}
          />
          <TextField
            label="Tittel (urdu)"
            lang="ur"
            dir="rtl"
            maxLength={80}
            value={f.kidTitleUr}
            onChange={(e) => onChange({ kidTitleUr: e.target.value })}
          />
          <TextField
            label="Tittel (arabisk)"
            lang="ar"
            dir="rtl"
            maxLength={80}
            value={f.kidTitleAr}
            onChange={(e) => onChange({ kidTitleAr: e.target.value })}
          />
          <Textarea
            label="Tekst (norsk)"
            rows={2}
            maxLength={300}
            value={f.kidText}
            onChange={(e) => onChange({ kidText: e.target.value })}
          />
          <Textarea
            label="Tekst (engelsk)"
            rows={2}
            maxLength={300}
            value={f.kidTextEn}
            onChange={(e) => onChange({ kidTextEn: e.target.value })}
          />
          <Textarea
            label="Tekst (urdu)"
            lang="ur"
            dir="rtl"
            rows={2}
            maxLength={300}
            value={f.kidTextUr}
            onChange={(e) => onChange({ kidTextUr: e.target.value })}
          />
          <Textarea
            label="Tekst (arabisk)"
            lang="ar"
            dir="rtl"
            rows={2}
            maxLength={300}
            value={f.kidTextAr}
            onChange={(e) => onChange({ kidTextAr: e.target.value })}
          />
        </div>
        <div className={styles.row3}>
          <TextField
            label="Emoji"
            maxLength={16}
            value={f.emoji}
            onChange={(e) => onChange({ emoji: e.target.value })}
          />
          <TextField
            label="Stjerner"
            type="number"
            min={0}
            max={10}
            value={String(f.stars)}
            onChange={(e) => onChange({ stars: Number(e.target.value) || 0 })}
          />
          <Toggle
            label="Aktiv (vises i appene)"
            checked={f.active}
            onChange={(e) => onChange({ active: e.target.checked })}
          />
        </div>

        {f.type === "DUA" && (
          <>
            <h4 className={styles.section}>Dua</h4>
            <DuaPicker selected={selectedDua} onSelect={onSelectDua} />
            <h4 className={styles.section}>Barnevennlig tekst (valgfritt)</h4>
            <p className={styles.muted}>
              Endrer bare det barna ser. Duaen under «Duaer» er uendret. Tomme
              felter bruker duaens tekst.
            </p>
            <Textarea
              label="Arabisk for barn"
              lang="ar"
              dir="rtl"
              rows={2}
              className={styles.arabicInput}
              placeholder={placeholder(
                selectedDua?.arabisk1 || selectedDua?.arabisk,
              )}
              value={f.arabicOverride}
              onChange={(e) => onChange({ arabicOverride: e.target.value })}
            />
            <TextField
              label="Transliterasjon for barn"
              placeholder={placeholder(selectedDua?.transliteration)}
              value={f.transliterationOverride}
              onChange={(e) =>
                onChange({ transliterationOverride: e.target.value })
              }
            />
            <div className={styles.row3}>
              <Textarea
                label="Betydning (norsk)"
                rows={2}
                placeholder={placeholder(selectedDua?.oversettelse)}
                value={f.meaningOverrideNb}
                onChange={(e) =>
                  onChange({ meaningOverrideNb: e.target.value })
                }
              />
              <Textarea
                label="Betydning (engelsk)"
                rows={2}
                placeholder={placeholder(selectedDua?.engelsk)}
                value={f.meaningOverrideEn}
                onChange={(e) =>
                  onChange({ meaningOverrideEn: e.target.value })
                }
              />
              <Textarea
                label="Betydning (urdu)"
                lang="ur"
                dir="rtl"
                rows={2}
                placeholder={placeholder(selectedDua?.urdu)}
                value={f.meaningOverrideUr}
                onChange={(e) =>
                  onChange({ meaningOverrideUr: e.target.value })
                }
              />
            </div>
          </>
        )}

        {f.type === "SURAH" && (
          <TextField
            label="Surahnummer"
            type="number"
            min={1}
            max={114}
            help="Ayahene og lyden hentes fra Koranen i appen."
            value={f.surahNumber}
            onChange={(e) => onChange({ surahNumber: e.target.value })}
          />
        )}

        {f.type === "LEARN" && (
          <Select
            label="Hva barnet lærer"
            value={f.learnKey}
            onChange={(e) => onChange({ learnKey: e.target.value })}
            options={[
              { value: "", label: "Velg …" },
              ...LEARN_KEYS.map((k) => ({ value: k, label: LEARN_LABELS[k] })),
            ]}
          />
        )}

        {f.type === "ADHAN" && (
          <>
            <h4 className={styles.section}>Adhan-fraser</h4>
            <AdhanPhrasesEditor
              phrases={f.phrases}
              onChange={(phrases) => onChange({ phrases })}
              itemId={f.id}
            />
          </>
        )}

        <h4 className={styles.section}>Lyd</h4>
        <KidsAudioField
          label={
            f.type === "ADHAN"
              ? "Hele adhan"
              : f.type === "DUA"
                ? "Lyd for barnet"
                : "Egen barnelyd (valgfri)"
          }
          value={f.audioUrlOverride}
          onChange={(url) => onChange({ audioUrlOverride: url })}
          itemId={f.id}
          fallbackUrl={
            f.type === "DUA"
              ? selectedDua?.audioUrl
              : f.type === "ADHAN"
                ? defaultAdhanAudioUrl
                : null
          }
          fallbackLabel={
            f.type === "DUA" ? "Duaens lyd (felles fil)" : "Standard adhan"
          }
          actions={
            f.type === "DUA" &&
            !editing.isNew &&
            !f.audioUrlOverride &&
            selectedDua?.audioUrl &&
            selectedDua.id === editing.initial.duaId ? (
              <Button
                type="button"
                size="sm"
                variant="mint"
                onClick={onCopyDuaAudio}
              >
                Lag egen barnekopi av lyden
              </Button>
            ) : null
          }
        />
        {f.type === "DUA" && !f.audioUrlOverride && (
          <p className={styles.muted}>
            Uten egen barnelyd bruker barna duaens felles lydfil. Lag en
            barnekopi eller last opp en barnevennlig fil, så kan du endre
            barnelyden uten å påvirke «Duaer».
          </p>
        )}

        <div className={styles.editorActions}>
          <Button
            onClick={onSave}
            loading={saving}
            disabled={!dirty && !editing.isNew}
          >
            Lagre
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Avbryt
          </Button>
          {dirty && <span className={styles.unsaved}>Ulagrede endringer</span>}
          <span className={styles.spacer} />
          {!editing.isNew && (
            <Button
              variant="danger"
              icon={<Icon name="trash" size={14} />}
              onClick={onDelete}
            >
              Slett
            </Button>
          )}
        </div>
      </Card>

      <div className={styles.previewCol}>
        <h4 className={styles.section}>Slik ser det ut i appen</h4>
        <KidsPreview
          form={f}
          dua={selectedDua}
          levelColor={levelColor}
          defaultAdhanAudioUrl={defaultAdhanAudioUrl}
        />
      </div>
    </div>
  );
};

export default KidsPage;
